// import { Linter as BaseLinter } from '@camunda/linting';
import { makeStyles } from '@fluentui/react-components';
import { memo, useEffect, useRef } from 'react';
import { v7 as uuidv7 } from 'uuid';
import { triggerErrorMessage } from '../message-bar-group/MessageBarGroup';
import { triggerModelerIsDirty } from '../modeler/react/context/ModelerIsDirtyContext';
import { useModelerContext } from '../modeler/react/context/useModelerContext';
import {
    useProcessDefinitionIdParam,
    useProcessDefinitionSiteContext,
} from './process-definition-api';
import { useProcessDefinitionQueryFragment } from './process-definition-query';
import { useRouterState } from '@tanstack/react-router';
import { useThemeContext } from '../theme/ThemeContext';

/*class Linter extends BaseLinter {
    async _createResolver(configName: string) {
        // console.log('###', configName);
        // const { configs } =
        await import('bpmnlint-plugin-camunda-compat');
        // console.log(configs[configName]);
        return super._createResolver(configName);
    }
}*/

export const ProcessDefinitionModeler = memo(() => {
    const modeler = useModelerContext();
    const modelerRef = useRef(modeler);
    modelerRef.current = modeler;
    const modelerContainerRef = useRef<HTMLDivElement>(null);
    const modelerPropertiesPanelRef = useRef<HTMLDivElement>(null);
    const styles = useStyles();
    const queryFragment = useProcessDefinitionQueryFragment(false);
    const id = useProcessDefinitionIdParam();
    const { mode } = useThemeContext();
    const site = useProcessDefinitionSiteContext();
    const xml = useRouterState({ select: (state) => state.location.state.xml });

    // effect to attach modeler and properties panel
    useEffect(() => {
        modeler.attachTo(modelerContainerRef.current!);
        const propertiesPanel = modeler.get('propertiesPanel');
        propertiesPanel.attachTo(modelerPropertiesPanelRef.current!);
        // modeler.get('linting').activate(); // TODO: declare types
        // modeler.get('linting').setActivate(); // TODO: declare types

        const eventBus = modeler.get('eventBus');
        const eventEC: Parameters<typeof eventBus.on<'elements.changed'>>[1] = () => {
            // @ts-ignore
            triggerModelerIsDirty(modeler.get('commandStack')?._stackIdx !== -1);
        };
        eventBus.on('elements.changed', eventEC);

        return () => {
            eventBus.off('elements.changed', eventEC);
            propertiesPanel.detach();
            modeler.detach();
        };
    }, [modeler]);

    // effect to import or create xml
    useEffect(() => {
        const modeler = modelerRef.current;
        copyOrCreateDiagram(id, queryFragment, site, xml)
            .then((xml) => modeler.importXML(xml))
            .catch((error: Error) => {
                triggerErrorMessage(
                    error.message,
                    'Fehler beim Importieren einer Prozessdefinition'
                );
                return modeler.importXML(createDiagram(site));
            })
            .finally(() => {
                triggerModelerIsDirty(false);
            });
        /*.finally(async () => {
                const canvas = modeler.get('canvas');
                canvas.zoom('fit-viewport');
                const rootElement = canvas.getRootElement() as Element;

                if (rootElement.type === 'bpmn:Process') {
                    modeler.get('modeling').updateProperties(rootElement, { isExecutable: true });
                }

                return;
                // console.log('######', modeler.get('linting'));
                const linter = new Linter({
                    modeler: 'web',
                    type: 'platform'
                });
                const reports = await linter.lint((await modeler.saveXML()).xml);;
                console.log('######', reports);
                modeler.get('linting').setErrors(reports);
            });*/
    }, [id, queryFragment, site, xml]);

    return (
        <div className={mode === 'dark' ? 'fi-bpmn-container-dark' : 'fi-bpmn-container-light'}>
            <main className={styles.root}>
                <div
                    aria-label="Beriech mit einem BPMN-Diagramm"
                    // className={styles.modeler}
                    ref={modelerContainerRef}
                />
                <div
                    aria-label="Bereich mit Eigenschaften eines BPMN-Diagram-Elements"
                    className={styles.modelerPropPanel}
                    ref={modelerPropertiesPanelRef}
                />
            </main>
        </div>
    );
});
ProcessDefinitionModeler.displayName = 'ProcessDefinitionModeler';

const copyOrCreateDiagram = (
    id: string,
    queryFragment: ReturnType<typeof useProcessDefinitionQueryFragment>,
    site: ISiteContext,
    xml?: string
) =>
    new Promise<string>((resolve, reject) => {
        try {
            if (id === '-1' && xml) {
                resolve(xml);
                return;
            }

            if (queryFragment) {
                resolve(queryFragment.definitionXml);
            } else {
                resolve(createDiagram(site));
            }
        } catch (e) {
            reject(e as Error);
        }
    });

const createDiagram = (site: ISiteContext): string => {
    const id = uuidv7();
    return `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions
    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
    xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
    xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
    xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
    xmlns:camunda="http://camunda.org/schema/1.0/bpmn"
    xmlns:fism="http://fism"
    targetNamespace="http://bpmn.io/schema/bpmn"
    id="Definitions_1"
>
    <bpmn:process
        camunda:historyTimeToLive="P7D"
        id="Process_${id}"
        isExecutable="true"
        fism:startPriority="500"
        fism:siteId="${site.siteId}"
        fism:siteName="${site.name}"
        fism:siteUrl="${site.siteUrl}"
    >
        <bpmn:startEvent id="StartEvent_1"/>
    </bpmn:process>
    <bpmndi:BPMNDiagram id="BPMNDiagram_1">
        <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_${id}">
            <bpmndi:BPMNShape id="_BPMNShape_StartEvent_2" bpmnElement="StartEvent_1">
                <dc:Bounds height="36.0" width="36.0" x="173.0" y="102.0"/>
            </bpmndi:BPMNShape>
        </bpmndi:BPMNPlane>
    </bpmndi:BPMNDiagram>
</bpmn:definitions>`;
};

const useStyles = makeStyles({
    /*modeler: {
        flexGrow: 1,
    },*/
    modelerPropPanel: {
        // overflow: 'auto',
        width: '350px',
        maxHeight: 'calc(100vh - 150px)',
    },
    root: {
        height: '100%',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        minWidth: 0, // 🔥 sehr wichtig
    },
});
